<template>
  <div class="model-comparison-list">
    <a-page-header title="模型横向对比" sub-title="多模型并行测试对比">
      <template #extra>
        <a-button type="primary" @click="showCreateModal = true">
          <template #icon><PlusOutlined /></template>
          新建计划
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="plans"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <a @click="viewDetail(record.id)">{{ record.name }}</a>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tooltip :title="record.status === 'failed' && record.error_message ? record.error_message : ''">
              <a-tag :color="getStatusColor(record.status)">
                {{ getStatusText(record.status) }}
              </a-tag>
            </a-tooltip>
          </template>
          <template v-else-if="column.key === 'models_count'">
            {{ record.models_count ?? '-' }}
          </template>
          <template v-else-if="column.key === 'cases_count'">
            {{ record.cases_count ?? '-' }}
          </template>
          <template v-else-if="column.key === 'created_at'">
            {{ formatTime(record.created_at) }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewDetail(record.id)">查看</a-button>
              <a-tooltip
                :title="record.status !== 'running' && (!record.models_count || !record.cases_count) ? '请先在详情页添加模型和测试用例' : ''"
              >
                <a-popconfirm
                  title="确定要执行此对比计划吗？"
                  @confirm="handleExecute(record.id)"
                  :disabled="record.status === 'running' || !record.models_count || !record.cases_count"
                >
                  <a-button
                    type="primary"
                    size="small"
                    :disabled="record.status === 'running' || !record.models_count || !record.cases_count"
                  >
                    {{ record.status === 'running' ? '执行中' : '执行' }}
                  </a-button>
                </a-popconfirm>
              </a-tooltip>
              <a-popconfirm
                title="确定要删除此对比计划吗？"
                @confirm="handleDelete(record.id)"
              >
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 新建计划弹窗 -->
    <a-modal
      v-model:open="showCreateModal"
      title="新建对比计划"
      @ok="handleCreate"
      :confirm-loading="creating"
      @cancel="resetCreateForm"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="名称" required>
          <a-input v-model:value="createForm.name" placeholder="请输入计划名称" />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="createForm.description" :rows="3" placeholder="请输入描述（可选）" />
        </a-form-item>
        <a-form-item label="每模型执行次数" required>
          <a-input-number
            v-model:value="createForm.runs_per_model"
            :min="1"
            style="width: 100%"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import {
  getComparisonPlans,
  createComparisonPlan,
  deleteComparisonPlan,
  executeComparisonPlan
} from '@/api/modelComparison';

const router = useRouter();

const plans = ref([]);
const loading = ref(false);
const showCreateModal = ref(false);
const creating = ref(false);

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0
});

const createForm = reactive({
  name: '',
  description: '',
  runs_per_model: 1
});

const columns = [
  { title: '名称', dataIndex: 'name', key: 'name', width: 200 },
  { title: '状态', dataIndex: 'status', key: 'status', width: 120 },
  { title: '模型数', dataIndex: 'models_count', key: 'models_count', width: 100 },
  { title: '用例数', dataIndex: 'cases_count', key: 'cases_count', width: 100 },
  { title: '每模型执行次数', dataIndex: 'runs_per_model', key: 'runs_per_model', width: 140 },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180 },
  { title: '操作', key: 'action', width: 220, fixed: 'right' }
];

const fetchPlans = async () => {
  loading.value = true;
  try {
    const res = await getComparisonPlans({
      page: pagination.current,
      pageSize: pagination.pageSize
    });
    if (res.code === 200) {
      plans.value = res.data?.items || [];
      pagination.total = res.data?.total || 0;
    }
  } catch (error) {
    message.error('获取计划列表失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const resetCreateForm = () => {
  Object.assign(createForm, {
    name: '',
    description: '',
    runs_per_model: 1
  });
};

const handleCreate = async () => {
  if (!createForm.name.trim()) {
    message.warning('请输入计划名称');
    return;
  }
  creating.value = true;
  try {
    const res = await createComparisonPlan({ ...createForm });
    message.success('创建成功，请在详情页添加模型和测试用例');
    showCreateModal.value = false;
    resetCreateForm();
    // 跳转到详情页，引导用户完成配置
    if (res.data?.id) {
      router.push(`/eval-v2/model-comparison/${res.data.id}`);
    } else {
      fetchPlans();
    }
  } catch (error) {
    message.error('创建失败：' + error.message);
  } finally {
    creating.value = false;
  }
};

const handleExecute = async (id) => {
  try {
    await executeComparisonPlan(id);
    message.success('执行已启动');
    fetchPlans();
  } catch (error) {
    message.error('执行失败：' + error.message);
  }
};

const handleDelete = async (id) => {
  try {
    await deleteComparisonPlan(id);
    message.success('删除成功');
    fetchPlans();
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

const viewDetail = (id) => {
  router.push(`/eval-v2/model-comparison/${id}`);
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchPlans();
};

const getStatusColor = (status) => {
  const colors = {
    draft: 'default',
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    draft: '草稿',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已停止'
  };
  return texts[status] || status;
};

const formatTime = (time) => {
  if (!time) return '-';
  return new Date(time).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

onMounted(() => {
  fetchPlans();
});
</script>

<style scoped>
.model-comparison-list {
  padding: 24px;
}
</style>
